import React, { useState, useEffect } from "react";
import { addTrip, generateTripId, updateTrip, deleteTrip, checkTripIdExists } from "../../functions/AddTrips";
import { fetchAllTrips, fetchTripById } from "../../functions/FetchTrips";
import { uploadToCloudinary } from "../../functions/Cloudinary";

const Trips = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [tripId, setTripId] = useState("");
  const [idExists, setIdExists] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [trips, setTrips] = useState([]);
  const [isLoadingTrips, setIsLoadingTrips] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add New Trip");

  const [cloudinaryCloudName, setCloudinaryCloudName] = useState("dcrrbjvcs");
  const [cloudinaryUploadPreset, setCloudinaryUploadPreset] = useState("sywtours");

  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [coverUploadMessage, setCoverUploadMessage] = useState({ text: "", type: "" });
  const [isUploadingBrochure, setIsUploadingBrochure] = useState(false);
  const [brochureUploadMessage, setBrochureUploadMessage] = useState({ text: "", type: "" });
  const [isUploadingAdditional, setIsUploadingAdditional] = useState(false);
  const [additionalUploadMessage, setAdditionalUploadMessage] = useState({ text: "", type: "" });

  const [coverImageFile, setCoverImageFile] = useState(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  const [brochureFile, setBrochureFile] = useState(null);

  const [formData, setFormData] = useState({
    tripId: "",
    tripName: "",
    description: "",
    destination: "",
    days: "",
    nights: "",
    pricing: [{ category: "Couple", price: "" }],
    category: "Popular",
    status: "Active",
    displayPrice: "",
    coverImageUrl: "",
    additionalImageUrls: [],
    brochureUrl: "",
    inclusions: [""],
    exclusions: [""],
    batches: {},
    pickup_points: {},
    dropping_points: {},
    itinerary: { day1: { title: "", description: "", meals: { B: false, L: false, D: false } } },
    notes: [""]
  });

  const [itineraryDayCount, setItineraryDayCount] = useState(1);

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    tripId: null,
    tripName: ""
  });

  const tabs = [
    { id: 1, name: "Basic Details" },
    { id: 2, name: "Media & Content" },
    { id: 3, name: "Trip Schedule" }
  ];

  useEffect(() => {
    if (!showAddModal) {
      setActiveTab(1);
      setIsEditMode(false);
      setModalTitle("Add New Trip");
      setCoverImageFile(null);
      setAdditionalImageFiles([]);
      setBrochureFile(null);
    }
  }, [showAddModal]);

  useEffect(() => {
    if (showAddModal && !isEditMode) {
      setTripId("");
      setFormData(prev => ({
        ...prev,
        tripId: "",
        tripName: "",
        description: "",
        destination: "",
        days: "",
        nights: "",
        pricing: [{ category: "Couple", price: "" }],
        category: "Popular",
        status: "Active",
        displayPrice: "",
        coverImageUrl: "",
        additionalImageUrls: [],
        brochureUrl: "",
        inclusions: [""],
        exclusions: [""],
        batches: {},
        pickup_points: {},
        dropping_points: {},
        itinerary: { day1: { title: "", description: "", meals: { B: false, L: false, D: false } } },
        notes: [""]
      }));
      setItineraryDayCount(1);
      setCoverImageFile(null);
      setAdditionalImageFiles([]);
      setBrochureFile(null);
    }
  }, [showAddModal, isEditMode]);

  useEffect(() => {
    const generateId = async () => {
      if (formData.tripName && formData.days && formData.nights) {
        const newTripId = await generateTripId(formData.tripName, formData.days, formData.nights);

        if (isEditMode) {
          if (newTripId !== formData.tripId) {
            const exists = await checkTripIdExists(newTripId);
            setIdExists(exists);
            setTripId(newTripId);
          }
        } else {
          const exists = await checkTripIdExists(newTripId);
          setIdExists(exists);
          setTripId(newTripId);
          setFormData(prev => ({ ...prev, tripId: newTripId }));
        }
      }
    };
    generateId();
  }, [formData.tripName, formData.days, formData.nights, isEditMode, formData.tripId]);

  useEffect(() => {
    if (formData.days && Number(formData.days) > itineraryDayCount) {
      addItineraryDays(Number(formData.days) - itineraryDayCount);
    }
  }, [formData.days]);

  useEffect(() => {
    const getTrips = async () => {
      setIsLoadingTrips(true);
      try {
        const tripsData = await fetchAllTrips();
        setTrips(tripsData);
      } catch (error) {
        // console.error("Error fetching trips:", error);
      } finally {
        setIsLoadingTrips(false);
      }
    };

    getTrips();
  }, []);

  useEffect(() => {
    const numInputs = document.querySelectorAll('input[type="number"]');

    numInputs.forEach(input => {
      input.addEventListener('wheel', preventScrollInput, { passive: false });
    });

    return () => {
      numInputs.forEach(input => {
        input.removeEventListener('wheel', preventScrollInput);
      });
    };
  }, [showAddModal, activeTab]);

  const refreshTrips = async () => {
    try {
      const tripsData = await fetchAllTrips();
      setTrips(tripsData);
    } catch (error) {
      // console.error("Error refreshing trips:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files && files.length > 0 ? files[0] : null;
      if (name === "coverImageFile") {
        setCoverImageFile(file);
        setCoverUploadMessage({ text: "", type: "" });
      } else if (name === "brochureFile") {
        setBrochureFile(file);
        setBrochureUploadMessage({ text: "", type: "" });
      }
    } else if (name === "cloudinaryCloudName") {
      setCloudinaryCloudName(value);
    } else if (name === "cloudinaryUploadPreset") {
      setCloudinaryUploadPreset(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleItineraryChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      itinerary: { ...prev.itinerary, [name]: value }
    }));
  };

  const handleDayTitleChange = (dayKey, value) => {
    setFormData(prev => ({
      ...prev,
      itinerary: { 
        ...prev.itinerary, 
        [dayKey]: {
          ...prev.itinerary[dayKey],
          title: value,
        } 
      }
    }));
  };

  const handleDayDescriptionChange = (dayKey, value) => {
    setFormData(prev => ({
      ...prev,
      itinerary: { 
        ...prev.itinerary, 
        [dayKey]: {
          ...prev.itinerary[dayKey],
          description: value,
        } 
      }
    }));
  };

  const handleDayMealsChange = (dayKey, mealType) => {
    setFormData(prev => ({
      ...prev,
      itinerary: {
        ...prev.itinerary,
        [dayKey]: {
          ...prev.itinerary[dayKey],
          meals: {
            ...((prev.itinerary[dayKey] && prev.itinerary[dayKey].meals) || { B: false, L: false, D: false }),
            [mealType]: !((prev.itinerary[dayKey] && prev.itinerary[dayKey].meals && prev.itinerary[dayKey].meals[mealType]) || false)
          }
        }
      }
    }));
  };

  const preventScrollInput = (e) => {
    e.target.blur();
  };

  const addItineraryDay = () => {
    const newDayCount = itineraryDayCount + 1;
    setItineraryDayCount(newDayCount);

    const newDayKey = `day${newDayCount}`;

    setFormData(prev => ({
      ...prev,
      itinerary: {
        ...prev.itinerary,
        [newDayKey]: {
          title: "",
          description: "",
          meals: { B: false, L: false, D: false }
        }
      }
    }));
  };

  const addItineraryDays = (count) => {
    let newItinerary = { ...formData.itinerary };
    let newDayCount = itineraryDayCount;

    for (let i = 0; i < count; i++) {
      newDayCount++;
      newItinerary[`day${newDayCount}`] = {
        title: "",
        description: "",
        meals: { B: false, L: false, D: false }
      };
    }

    setItineraryDayCount(newDayCount);
    setFormData(prev => ({
      ...prev,
      itinerary: newItinerary
    }));
  };

  const removeItineraryDay = (dayKey) => {
    const newItinerary = { ...formData.itinerary };
    delete newItinerary[dayKey];

    if (dayKey === `day${itineraryDayCount}`) {
      setItineraryDayCount(prev => prev - 1);
    }

    setFormData(prev => ({
      ...prev,
      itinerary: newItinerary
    }));
  };

  const handleArrayItemChange = (index, value, field, type = "text", files = null) => {
    if (field === 'additionalImageUrls' && type === 'file') {
      const file = files && files.length > 0 ? files[0] : null;
      const newFiles = [...additionalImageFiles];
      newFiles[index] = file;
      setAdditionalImageFiles(newFiles);
    } else {
      const newArray = [...formData[field]];
      newArray[index] = value;
      setFormData(prev => ({ ...prev, [field]: newArray }));
    }
  };

  const addArrayItem = (field) => {
    if (field === 'additionalImageUrls') {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], ""]
      }));
      setAdditionalImageFiles(prev => [...prev, null]);
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], ""]
      }));
    }
  };

  const removeArrayItem = (index, field) => {
    if (field === 'additionalImageUrls') {
      const newUrls = [...formData[field]];
      newUrls.splice(index, 1);
      setFormData(prev => ({ ...prev, [field]: newUrls }));

      const newFiles = [...additionalImageFiles];
      newFiles.splice(index, 1);
      setAdditionalImageFiles(newFiles);
    } else {
      const newArray = [...formData[field]];
      newArray.splice(index, 1);
      setFormData(prev => ({ ...prev, [field]: newArray }));
    }
  };

  const handlePricingChange = (index, field, value) => {
    const newPricing = [...formData.pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    setFormData(prev => ({ ...prev, pricing: newPricing }));
  };

  const addPricingCategory = () => {
    setFormData(prev => ({
      ...prev,
      pricing: [...prev.pricing, { category: "", price: "" }]
    }));
  };

  const removePricingCategory = (index) => {
    const newPricing = [...formData.pricing];
    newPricing.splice(index, 1);
    setFormData(prev => ({ ...prev, pricing: newPricing }));
  };

  const nextTab = (e) => {
    e.preventDefault();
    if (activeTab < tabs.length) {
      setActiveTab(activeTab + 1);
    }
  };

  const prevTab = (e) => {
    e.preventDefault();
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  const changeTab = (e, tabId) => {
    e.preventDefault();
    setActiveTab(tabId);
  };

  const handleEditTrip = async (tripId) => {
    try {
      setIsLoading(true);
      const tripData = await fetchTripById(tripId);

      if (!tripData) {
        // console.error("Trip not found");
        return;
      }

      const pricingArray = Object.entries(tripData.starting_price || {}).map(
        ([category, price]) => ({ category, price })
      );

      if (pricingArray.length === 0) {
        pricingArray.push({ category: "", price: "" });
      }

      const itineraryDays = Object.keys(tripData.Itinerary || {}).length;

      let additionalImages = tripData.additional_image_url || [];
      if (!Array.isArray(additionalImages)) {
        additionalImages = typeof additionalImages === 'string' ? [additionalImages] : [];
      }

      const itineraryData = Object.entries(tripData.Itinerary || {}).reduce((acc, [dayKey, dayValue]) => {
        // If legacy string, convert to new format
        if (typeof dayValue === 'string') {
          acc[dayKey] = { title: "", description: dayValue, meals: { B: false, L: false, D: false } };
        } else {
          acc[dayKey] = {
            title: dayValue.title || "",
            description: dayValue.description || "",
            meals: dayValue.meals || { B: false, L: false, D: false }
          };
        }
        return acc;
      }, {});

      setFormData({
        tripId: tripData.id,
        tripName: tripData.tripname || "",
        description: tripData.description || "",
        displayPrice: tripData.display_price || "",
        destination: tripData.location || "",
        days: tripData.days || "",
        nights: tripData.nights || "",
        pricing: pricingArray,
        category: tripData.category || "Popular",
        status: tripData.status || "Active",
        coverImageUrl: tripData.cover_image_url || "",
        additionalImageUrls: additionalImages,
        brochureUrl: tripData.brochure_url || "",
        inclusions: tripData.inclusions && tripData.inclusions.length > 0 ? tripData.inclusions : [""],
        exclusions: tripData.exclusions && tripData.exclusions.length > 0 ? tripData.exclusions : [""],
        batches: tripData.batches || {},
        pickup_points: tripData.pickup_points || {},
        dropping_points: tripData.dropping_points || {},
        itinerary: itineraryData,
        notes: tripData.notes && tripData.notes.length > 0 ? tripData.notes : [""]
      });

      setCoverImageFile(null);
      setAdditionalImageFiles(Array(additionalImages.length).fill(null));
      setBrochureFile(null);

      setItineraryDayCount(itineraryDays || 1);

      setIsEditMode(true);
      setTripId(tripData.id);
      setModalTitle("Edit Trip");

      setShowAddModal(true);
    } catch (error) {
      // console.error("Error preparing trip for edit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTrip = (tripId, tripName) => {
    setDeleteModal({
      show: true,
      tripId,
      tripName
    });
  };

  const confirmDeleteTrip = async () => {
    try {
      setIsLoading(true);

      const { success, message } = await deleteTrip(deleteModal.tripId);

      if (success) {
        setMessage({ text: message, type: "success" });
        await refreshTrips();
      } else {
        setMessage({ text: message, type: "danger" });
      }
    } catch (error) {
      setMessage({ text: error.message, type: "danger" });
    } finally {
      setDeleteModal({ show: false, tripId: null, tripName: "" });
      setIsLoading(false);

      setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 3000);
    }
  };

  const getFinalTripId = () => {
    return (isEditMode && tripId !== formData.tripId) ? tripId : formData.tripId;
  };

  const handleUploadCover = async () => {
    const finalTripId = getFinalTripId();
    if (!coverImageFile || !cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId) {
      setCoverUploadMessage({ text: "Missing file, Cloudinary details, or Trip ID.", type: "warning" });
      return;
    }
    setIsUploadingCover(true);
    setCoverUploadMessage({ text: "Uploading...", type: "info" });
    try {
      const uploadedUrl = await uploadToCloudinary(
        coverImageFile,
        cloudinaryCloudName,
        cloudinaryUploadPreset,
        finalTripId
      );
      setFormData(prev => ({ ...prev, coverImageUrl: uploadedUrl }));
      setCoverImageFile(null);
      setCoverUploadMessage({ text: "Cover image uploaded successfully!", type: "success" });
    } catch (error) {
      setCoverUploadMessage({ text: `Upload failed: ${error.message}`, type: "danger" });
    } finally {
      setIsUploadingCover(false);
      setTimeout(() => setCoverUploadMessage({ text: "", type: "" }), 5000);
    }
  };

  const handleUploadBrochure = async () => {
    const finalTripId = getFinalTripId();
    if (!brochureFile || !cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId) {
      setBrochureUploadMessage({ text: "Missing file, Cloudinary details, or Trip ID.", type: "warning" });
      return;
    }
    setIsUploadingBrochure(true);
    setBrochureUploadMessage({ text: "Uploading...", type: "info" });
    try {
      const uploadedUrl = await uploadToCloudinary(
        brochureFile,
        cloudinaryCloudName,
        cloudinaryUploadPreset,
        finalTripId
      );
      setFormData(prev => ({ ...prev, brochureUrl: uploadedUrl }));
      setBrochureFile(null);
      setBrochureUploadMessage({ text: "Brochure uploaded successfully!", type: "success" });
    } catch (error) {
      setBrochureUploadMessage({ text: `Upload failed: ${error.message}`, type: "danger" });
    } finally {
      setIsUploadingBrochure(false);
      setTimeout(() => setBrochureUploadMessage({ text: "", type: "" }), 5000);
    }
  };

  const handleUploadAdditional = async () => {
    const finalTripId = getFinalTripId();
    if (!cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId) {
      setAdditionalUploadMessage({ text: "Missing Cloudinary details or Trip ID.", type: "warning" });
      return;
    }

    const filesToUpload = additionalImageFiles.map((file, index) => ({ file, index }))
      .filter(({ file, index }) => file && !formData.additionalImageUrls[index]?.includes('cloudinary'));

    if (filesToUpload.length === 0) {
      setAdditionalUploadMessage({ text: "No new additional images selected for upload.", type: "info" });
      setTimeout(() => setAdditionalUploadMessage({ text: "", type: "" }), 3000);
      return;
    }

    setIsUploadingAdditional(true);
    setAdditionalUploadMessage({ text: `Uploading ${filesToUpload.length} image(s)...`, type: "info" });

    let successCount = 0;
    let errorCount = 0;
    const uploadPromises = filesToUpload.map(async ({ file, index }) => {
      try {
        const uploadedUrl = await uploadToCloudinary(
          file,
          cloudinaryCloudName,
          cloudinaryUploadPreset,
          finalTripId
        );
        setFormData(prev => {
          const newUrls = [...prev.additionalImageUrls];
          newUrls[index] = uploadedUrl;
          return { ...prev, additionalImageUrls: newUrls };
        });
        successCount++;
      } catch (error) {
        // console.error(`Error uploading additional image ${index}:`, error);
        errorCount++;
      }
    });

    await Promise.all(uploadPromises);

    setIsUploadingAdditional(false);
    if (errorCount > 0) {
      setAdditionalUploadMessage({ text: `Uploaded ${successCount} image(s), ${errorCount} failed.`, type: "warning" });
    } else {
      setAdditionalUploadMessage({ text: `Successfully uploaded ${successCount} image(s).`, type: "success" });
    }
    setTimeout(() => setAdditionalUploadMessage({ text: "", type: "" }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalTripId = getFinalTripId();

    if (!finalTripId) {
      setMessage({ text: "Trip ID could not be determined. Please ensure Trip Name, Days, and Nights are filled.", type: "danger" });
      return;
    }

    if (idExists && (!isEditMode || (isEditMode && tripId !== formData.tripId))) {
      setMessage({
        text: "A trip with this ID already exists. Please change the trip name or duration.",
        type: "danger"
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "Saving trip details...", type: "info" });

    try {
      const pricingForFirestore = {};
      formData.pricing.forEach(item => {
        if (item.category && item.price) {
          pricingForFirestore[item.category] = Number(item.price);
        }
      });

      const tripDataForSubmission = {
        tripId: finalTripId,
        tripname: formData.tripName,
        description: formData.description.trim(),
        location: formData.destination,
        days: Number(formData.days) || 0,
        nights: Number(formData.nights) || 0,
        category: formData.category,
        status: formData.status,
        display_price: formData.displayPrice.trim(),
        starting_price: pricingForFirestore,
        cover_image_url: formData.coverImageUrl || "",
        additional_image_url: formData.additionalImageUrls.filter(url => url && url.trim() !== ""),
        brochure_url: formData.brochureUrl || "",
        inclusions: formData.inclusions.filter(item => item.trim() !== ""),
        exclusions: formData.exclusions.filter(item => item.trim() !== ""),
        notes: formData.notes.filter(item => item.trim() !== ""),
        batches: formData.batches || {},
        pickup_points: formData.pickup_points || {},
        dropping_points: formData.dropping_points || {},
        Itinerary: formData.itinerary || {}
      };

      let result;
      if (isEditMode) {
        result = await updateTrip(formData.tripId, tripDataForSubmission);
      } else {
        result = await addTrip(tripDataForSubmission);
      }

      if (result.success) {
        setMessage({ text: result.message, type: "success" });
        setFormData({
          tripId: "",
          tripName: "",
          description: "",
          destination: "",
          days: "",
          nights: "",
          pricing: [{ category: "Couple", price: "" }],
          category: "Popular",
          status: "Active",
          displayPrice: "",
          coverImageUrl: "",
          additionalImageUrls: [],
          brochureUrl: "",
          inclusions: [""],
          exclusions: [""],
          batches: {},
          pickup_points: {},
          dropping_points: {},
          itinerary: { day1: { title: "", description: "", meals: { B: false, L: false, D: false } } },
          notes: [""]
        });
        setCoverImageFile(null);
        setAdditionalImageFiles([]);
        setBrochureFile(null);
        setTripId("");
        setIdExists(false);
        setItineraryDayCount(1);

        await refreshTrips();

        setTimeout(() => {
          setShowAddModal(false);
          setMessage({ text: "", type: "" });
          setIsEditMode(false);
        }, 2000);
      } else {
        setMessage({ text: result.message || "An error occurred while saving.", type: "danger" });
      }
    } catch (error) {
      // console.error("Error during submit:", error);
      setMessage({ text: `An error occurred: ${error.message}`, type: "danger" });
    } finally {
      setIsLoading(false);
    }
  };

  const renderBasicDetailsTab = () => {
    return (
      <div className="tab-pane fade show active">
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Trip ID</label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                value={tripId}
                disabled
              />
              <div className="id-preview">
                {formData.tripName && formData.days && formData.nights ? (
                  isEditMode ? (
                    tripId !== formData.tripId ? (
                      !idExists ? (
                        <span className="text-info">
                          <i className="fas fa-info-circle me-1"></i>
                          ID will update to: {tripId}
                        </span>
                      ) : (
                        <span className="text-danger">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          New ID already exists
                        </span>
                      )
                    ) : (
                      <span className="text-muted">
                        <i className="fas fa-lock me-1"></i>
                        ID unchanged: {tripId}
                      </span>
                    )
                  ) : (
                    !idExists ? (
                      <span className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        ID will be: {tripId}
                      </span>
                    ) : (
                      <span className="text-danger">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        This ID already exists
                      </span>
                    )
                  )
                ) : (
                  <span className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Fill in trip name and duration to generate ID
                  </span>
                )}
              </div>
            </div>
            {((!isEditMode && idExists) || (isEditMode && idExists && tripId !== formData.tripId)) && (
              <div className="text-danger mt-1">
                <i className="fas fa-exclamation-triangle me-1"></i>
                This trip ID already exists. Please change the trip name or duration.
              </div>
            )}
            <div className="form-text">
              {isEditMode && (
                <span className="ms-1">
                  <strong>Note:</strong> Changing the trip name or duration will update the ID.
                </span>
              )}
            </div>
          </div>

          <div className="col-md-6 mb-2">
            <label className="form-label">Trip Name</label>
            <input
              type="text"
              className="form-control"
              name="tripName"
              value={formData.tripName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="col-md-12 mb-2">
            <label className="form-label">Trip Overview</label>
            <textarea
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="2"
              placeholder="Provide a brief description of the trip"
            ></textarea>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Destination</label>
              <input
                type="text"
                className="form-control"
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-md-3 col-6 mb-3">
              <label className="form-label">Days</label>
              <input
                type="number"
                className="form-control"
                name="days"
                value={formData.days}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                required
              />
            </div>

            <div className="col-md-3 col-6 mb-3">
              <label className="form-label">Nights</label>
              <input
                type="number"
                className="form-control"
                name="nights"
                value={formData.nights}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                required
              />
            </div>
          </div>

          <div className="row mx-0 px-0">
            <div className="col-md-4 col-6 mb-3 ps-md-3 ps-0">
              <label className="form-label">Trip Category</label>
              <input
                type="text"
                className="form-control"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="e.g. Popular"
                required
              />
            </div>

            <div className="col-md-4 col-6 mb-3 pe-md-3 pe-0">
              <label className="form-label">Display Price</label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  type="text"
                  className="form-control"
                  name="displayPrice"
                  value={formData.displayPrice}
                  onChange={handleInputChange}
                  placeholder="e.g. 12,999"
                />
              </div>
            </div>

            <div className="col-md-4 col-sm-12 mb-3 px-md-3 px-0">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="col-md-12 mb-4">
            <label className="form-label">Pricing Categories</label>
            <div className="pricing-categories">
              {formData.pricing.map((item, index) => (
                <div className="row mb-2 pricing-item" key={`pricing-${index}`}>
                  <div className="col-sm-5 col-7 mb-2 mb-sm-0">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Category name (e.g. Couple, Solo)"
                      value={item.category}
                      onChange={(e) => handlePricingChange(index, 'category', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-sm-5 col-7 mb-2 mb-sm-0">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Price (₹)"
                      value={item.price}
                      onChange={(e) => handlePricingChange(index, 'price', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      required
                    />
                  </div>
                  <div className="col-sm-2 col-5 text-end text-sm-start">
                    {formData.pricing.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removePricingCategory(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={addPricingCategory}
              >
                <i className="fas fa-plus me-1"></i> Add Pricing Category
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          {tabs.length > 1 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={nextTab}
            >
              Next <i className="fas fa-arrow-right ms-1"></i>
            </button>
          )}
          {tabs.length === 1 && (
            <button
              type="submit"
              className="btn btn-success"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {isEditMode ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i> {isEditMode ? "Update Trip" : "Save Trip"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const [batchStartDate, setBatchStartDate] = useState("");
  const [batchEndDate, setBatchEndDate] = useState("");

  const addBatch = () => {
    if (!batchStartDate || !batchEndDate) return;

    try {
      const startDate = new Date(batchStartDate);
      const endDate = new Date(batchEndDate);

      if (endDate < startDate) {
        alert("End date cannot be before start date");
        return;
      }

      const startDay = startDate.getDate();
      const endDay = endDate.getDate();
      const month = startDate.toLocaleString('default', { month: 'long' });
      const year = startDate.getFullYear();

      const monthYear = `${month} ${year}`;
      const dateRange = `${startDay}-${endDay}`;

      setFormData(prev => {
        const updatedBatches = { ...prev.batches };

        if (!updatedBatches[monthYear]) {
          updatedBatches[monthYear] = [];
        }

        if (!updatedBatches[monthYear].includes(dateRange)) {
          updatedBatches[monthYear] = [...updatedBatches[monthYear], dateRange];
        }

        return {
          ...prev,
          batches: updatedBatches
        };
      });

      setBatchStartDate("");
      setBatchEndDate("");
    } catch (error) {
      // console.error("Error adding batch:", error);
    }
  };

  const removeBatch = (month, date) => {
    setFormData(prev => {
      const updatedBatches = { ...prev.batches };
      updatedBatches[month] = updatedBatches[month].filter(d => d !== date);

      if (updatedBatches[month].length === 0) {
        delete updatedBatches[month];
      }

      return {
        ...prev,
        batches: updatedBatches
      };
    });
  };

  const renderMediaContentTab = () => {
    const additionalImageSlots = formData.additionalImageUrls || [];
    const allPreviews = [
      ...additionalImageSlots
        .map((url, idx) => (url && !additionalImageFiles[idx] ? { type: 'url', value: url, id: `url-${idx}` } : null))
        .filter(Boolean),
      ...additionalImageFiles
        .map((file, idx) => (file ? { type: 'file', value: file, id: `file-${idx}` } : null))
        .filter(Boolean),
    ];
    const finalTripId = getFinalTripId();

    return (
      <div className="tab-pane fade show active">
        <div className="row mb-4 p-3 bg-light border rounded">
          <h6 className="mb-3 text-primary"><i className="fas fa-cloud-upload-alt me-2"></i>Cloudinary Upload Settings</h6>
          <div className="col-md-6 mb-2">
            <label className="form-label">Cloud Name</label>
            <input
              type="text"
              className="form-control form-control-sm"
              name="cloudinaryCloudName"
              value={cloudinaryCloudName}
              onChange={handleInputChange}
              placeholder="Enter your Cloudinary cloud name"
            />
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">Upload Preset</label>
            <input
              type="text"
              className="form-control form-control-sm"
              name="cloudinaryUploadPreset"
              value={cloudinaryUploadPreset}
              onChange={handleInputChange}
              placeholder="Enter your unsigned upload preset name"
            />
          </div>
          {!cloudinaryCloudName || !cloudinaryUploadPreset ? (
            <div className="col-12 mt-1">
              <small className="text-muted">Enter your Cloudinary details to enable file uploads.</small>
            </div>
          ) : null}
        </div>

        <div className="row">
          <div className="col-12 mb-3">
            <h5 className="mb-3">Trip Images</h5>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Cover Image</label>
                <div className="input-group mb-1">
                  <input
                    type="file"
                    className="form-control"
                    name="coverImageFile"
                    accept="image/*"
                    onChange={handleInputChange}
                    disabled={isUploadingCover}
                  />
                  {(coverImageFile || formData.coverImageUrl) && !isUploadingCover && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setCoverImageFile(null); setFormData(prev => ({ ...prev, coverImageUrl: "" })); setCoverUploadMessage({ text: "", type: "" }); }} title="Clear image">
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary w-100"
                  onClick={handleUploadCover}
                  disabled={!coverImageFile || isUploadingCover || !cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId}
                >
                  {isUploadingCover ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Uploading...</>
                  ) : (
                    <><i className="fas fa-upload me-1"></i> Upload Cover</>
                  )}
                </button>
                {coverUploadMessage.text && (
                  <div className={`mt-1 small alert alert-${coverUploadMessage.type} py-1 px-2`}>
                    {coverUploadMessage.text}
                  </div>
                )}
                {coverImageFile && !formData.coverImageUrl && (
                  <div className="mt-2 text-muted small">Selected: {coverImageFile.name}</div>
                )}
                {formData.coverImageUrl && (
                  <div className="mt-2">
                    <span className="me-2 small">Current:</span>
                    <img src={formData.coverImageUrl} alt="Cover Preview" className="img-thumbnail" style={{ maxHeight: "80px" }} />
                  </div>
                )}
              </div>

              <div className="col-md-8 mb-3">
                <label className="form-label">Additional Images <small className="text-muted">(Multiple images allowed)</small></label>
                <div className="d-flex gap-2 mb-1">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        const currentFiles = additionalImageFiles.filter(f => f);
                        const newFiles = [...currentFiles, ...files];
                        setAdditionalImageFiles(newFiles);
                        setAdditionalUploadMessage({ text: "", type: "" });

                        const currentUrlCount = formData.additionalImageUrls.length;
                        const requiredSlots = newFiles.length;
                        const additionalSlotsNeeded = requiredSlots - currentUrlCount;

                        if (additionalSlotsNeeded > 0) {
                          setFormData(prev => ({
                            ...prev,
                            additionalImageUrls: [
                              ...prev.additionalImageUrls,
                              ...Array(additionalSlotsNeeded).fill("")
                            ]
                          }));
                        }
                      }
                      e.target.value = null;
                    }}
                    disabled={isUploadingAdditional}
                  />
                  {(additionalImageFiles.some(f => f) || additionalImageSlots.some(url => url)) && !isUploadingAdditional && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setAdditionalImageFiles([]); setFormData(prev => ({ ...prev, additionalImageUrls: [] })); setAdditionalUploadMessage({ text: "", type: "" }); }} title="Clear all images">
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary w-100 mb-2"
                  onClick={handleUploadAdditional}
                  disabled={additionalImageFiles.filter(f => f).length === 0 || isUploadingAdditional || !cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId}
                >
                  {isUploadingAdditional ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Uploading...</>
                  ) : (
                    <><i className="fas fa-upload me-1"></i> Upload New Additional Images</>
                  )}
                </button>
                {additionalUploadMessage.text && (
                  <div className={`mt-1 small alert alert-${additionalUploadMessage.type} py-1 px-2`}>
                    {additionalUploadMessage.text}
                  </div>
                )}
                {(allPreviews.length > 0) && (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label mb-0 small">Image Previews:</label>
                      <span className="text-muted small">{allPreviews.length} images</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {allPreviews.map((preview, idx) => (
                        <div key={preview.id} className="position-relative">
                          <img
                            src={preview.type === 'file' ? URL.createObjectURL(preview.value) : preview.value}
                            alt={`Additional ${idx + 1}`}
                            className="img-thumbnail"
                            style={{ height: "60px", width: "60px", objectFit: "cover" }}
                          />
                          {!isUploadingAdditional && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0"
                              style={{ width: "18px", height: "18px", fontSize: "9px", lineHeight: "0" }}
                              onClick={() => {
                                const originalIndex = preview.type === 'file'
                                  ? additionalImageFiles.findIndex(f => f === preview.value)
                                  : formData.additionalImageUrls.findIndex(u => u === preview.value);

                                if (originalIndex !== -1) {
                                  const newFiles = [...additionalImageFiles];
                                  const newUrls = [...formData.additionalImageUrls];
                                  newFiles.splice(originalIndex, 1);
                                  newUrls.splice(originalIndex, 1);
                                  setAdditionalImageFiles(newFiles);
                                  setFormData(prev => ({ ...prev, additionalImageUrls: newUrls }));
                                }
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-12 mb-4">
            <div className="row">
              <div className="col-md-6 mb-4">
                <label className="form-label">Brochure (PDF)</label>
                <div className="input-group mb-1">
                  <input
                    type="file"
                    className="form-control"
                    name="brochureFile"
                    accept=".pdf,application/pdf"
                    onChange={handleInputChange}
                    disabled={isUploadingBrochure}
                  />
                  {(brochureFile || formData.brochureUrl) && !isUploadingBrochure && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setBrochureFile(null); setFormData(prev => ({ ...prev, brochureUrl: "" })); setBrochureUploadMessage({ text: "", type: "" }); }} title="Clear brochure">
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary w-100"
                  onClick={handleUploadBrochure}
                  disabled={!brochureFile || isUploadingBrochure || !cloudinaryCloudName || !cloudinaryUploadPreset || !finalTripId}
                >
                  {isUploadingBrochure ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Uploading...</>
                  ) : (
                    <><i className="fas fa-upload me-1"></i> Upload Brochure</>
                  )}
                </button>
                {brochureUploadMessage.text && (
                  <div className={`mt-1 small alert alert-${brochureUploadMessage.type} py-1 px-2`}>
                    {brochureUploadMessage.text}
                  </div>
                )}
                {brochureFile && !formData.brochureUrl && (
                  <div className="mt-2 text-muted small">Selected: {brochureFile.name}</div>
                )}
                {formData.brochureUrl && (
                  <div className="mt-2 small">
                    Current: <a href={formData.brochureUrl} target="_blank" rel="noopener noreferrer">View Brochure</a>
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-4">
                {formData.notes.map((item, index) => (
                  <div className="input-group mb-2" key={`note-${index}`}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter note"
                      value={item}
                      onChange={(e) => handleArrayItemChange(index, e.target.value, 'notes')}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removeArrayItem(index, 'notes')}
                      disabled={formData.notes.length <= 1}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => addArrayItem('notes')}
                >
                  <i className="fas fa-plus me-1"></i> Add Note
                </button>
              </div>
            </div>
          </div>

          <div className="col-12">
            <hr className="my-4" />
            <h5 className="mb-3">Package Details</h5>
          </div>

          <div className="row">
            <div className="col-md-6 mb-4">
              <label className="form-label d-flex align-items-center">
                <i className="fas fa-check-circle text-success me-2"></i>
                Inclusions
              </label>
              {formData.inclusions.map((item, index) => (
                <div className="input-group mb-2" key={`inclusion-${index}`}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter inclusion item"
                    value={item}
                    onChange={(e) => handleArrayItemChange(index, e.target.value, 'inclusions')}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeArrayItem(index, 'inclusions')}
                    disabled={formData.inclusions.length <= 1}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => addArrayItem('inclusions')}
              >
                <i className="fas fa-plus me-1"></i> Add Inclusion
              </button>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label d-flex align-items-center">
                <i className="fas fa-times-circle text-danger me-2"></i>
                Exclusions
              </label>
              {formData.exclusions.map((item, index) => (
                <div className="input-group mb-2" key={`exclusion-${index}`}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter exclusion item"
                    value={item}
                    onChange={(e) => handleArrayItemChange(index, e.target.value, 'exclusions')}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeArrayItem(index, 'exclusions')}
                    disabled={formData.exclusions.length <= 1}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => addArrayItem('exclusions')}
              >
                <i className="fas fa-plus me-1"></i> Add Exclusion
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevTab}
          >
            <i className="fas fa-arrow-left me-1"></i> Previous
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={nextTab}
          >
            Next <i className="fas fa-arrow-right ms-1"></i>
          </button>
        </div>
      </div>
    );
  };

  const renderTripScheduleTab = () => {
    const sortedDayKeys = Object.keys(formData.itinerary || {}).sort((a, b) => {
      const aNum = parseInt(a.replace('day', ''));
      const bNum = parseInt(b.replace('day', ''));
      return aNum - bNum;
    });

    return (
      <div className="tab-pane fade show active">
        <div className="row">
          <div className="col-12 mb-4">
            <h5 className="mb-3">Trip Batches</h5>
            <p className="text-muted small mb-3">
              Add the available date ranges for this trip. Select start and end dates for each batch.
            </p>

            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <div className="date-input-wrapper" onClick={() => document.getElementById('batchStartDate').focus()}>
                  <input
                    id="batchStartDate"
                    type="date"
                    className="form-control"
                    value={batchStartDate}
                    onChange={(e) => setBatchStartDate(e.target.value)}
                  />
                  <i className="fas fa-calendar-alt date-icon"></i>
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">End Date</label>
                <div className="date-input-wrapper" onClick={() => document.getElementById('batchEndDate').focus()}>
                  <input
                    id="batchEndDate"
                    type="date"
                    className="form-control"
                    value={batchEndDate}
                    onChange={(e) => setBatchEndDate(e.target.value)}
                  />
                  <i className="fas fa-calendar-alt date-icon"></i>
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-primary form-control"
                  onClick={addBatch}
                  disabled={!batchStartDate || !batchEndDate}
                >
                  <i className="fas fa-plus me-1"></i> Add Batch
                </button>
              </div>
            </div>

            {Object.keys(formData.batches || {}).length > 0 ? (
              <div className="card">
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Dates</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(formData.batches || {}).map(([month, dates]) => (
                        <tr key={month}>
                          <td>{month}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {dates.map(date => (
                                <span
                                  key={`${month}-${date}`}
                                  className="badge bg-light text-dark border"
                                >
                                  {date}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {dates.map(date => (
                                <button
                                  key={`btn-${month}-${date}`}
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeBatch(month, date)}
                                  title={`Remove ${date}`}
                                >
                                  <i className="fas fa-times"></i> {date}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="alert alert-light">
                No batches added yet. Add batches to show available dates for this trip.
              </div>
            )}
          </div>

          <div className="col-12">
            <hr className="my-4" />
            <h5 className="mb-3">Day-by-Day Schedule</h5>
          </div>
          <div className="col-12 mb-3">
            {sortedDayKeys.map((dayKey) => {
              const dayNumber = parseInt(dayKey.replace('day', ''));
              const dayData = formData.itinerary[dayKey] || {};
              
              const isLegacyFormat = typeof dayData === 'string';
              const title = isLegacyFormat ? "" : dayData.title || "";
              const description = isLegacyFormat ? dayData : dayData.description || "";
              const meals = isLegacyFormat ? { B: false, L: false, D: false } : (dayData.meals || { B: false, L: false, D: false });

              return (
                <div className="card mb-3" key={dayKey}>
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <div className="day-header">
                      <span className="day-number">Day {dayNumber}</span>
                      {title && <span className="day-title ms-2">- {title}</span>}
                    </div>
                    {Object.keys(formData.itinerary).length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeItineraryDay(dayKey)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="mb-2 row align-items-center">
                      <div className="col-md-8 col-12">
                        <label className="form-label">Day Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={`Title for Day ${dayNumber} (e.g. "Arrival" or "Mountain Trek")`}
                          value={title}
                          onChange={(e) => handleDayTitleChange(dayKey, e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 col-12 mt-2 mt-md-0">
                        <label className="form-label mb-1">Meals</label>
                        <div className="meals-bg px-2 py-1 rounded">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`meals-b-${dayKey}`}
                              checked={meals.B}
                              onChange={() => handleDayMealsChange(dayKey, 'B')}
                            />
                            <label className="form-check-label" htmlFor={`meals-b-${dayKey}`}>B</label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`meals-l-${dayKey}`}
                              checked={meals.L}
                              onChange={() => handleDayMealsChange(dayKey, 'L')}
                            />
                            <label className="form-check-label" htmlFor={`meals-l-${dayKey}`}>L</label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`meals-d-${dayKey}`}
                              checked={meals.D}
                              onChange={() => handleDayMealsChange(dayKey, 'D')}
                            />
                            <label className="form-check-label" htmlFor={`meals-d-${dayKey}`}>D</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder={`Describe the activities for Day ${dayNumber}`}
                        value={description}
                        onChange={(e) => handleDayDescriptionChange(dayKey, e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={addItineraryDay}
              >
                <i className="fas fa-plus me-1"></i> Add Another Day
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevTab}
          >
            <i className="fas fa-arrow-left me-1"></i> Previous
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEditMode ? "Updating..." : "Saving..."}
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i> {isEditMode ? "Update Trip" : "Save Trip"}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 1:
        return renderBasicDetailsTab();
      case 2:
        return renderMediaContentTab();
      case 3:
        return renderTripScheduleTab();
      default:
        return renderBasicDetailsTab();
    }
  };

  return (
    <div className="trips-section fadeIn">
      <div className="admin-card">
        <div className="card-header">
          <h3>Manage Trips</h3>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <i className="fas fa-plus me-1"></i> Add New Trip
          </button>
        </div>
        <div className="card-body">
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          {isLoadingTrips ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading trips...</p>
            </div>
          ) : trips.length === 0 ? (
            <div className="alert alert-info text-center">
              No trips found. Add your first trip to get started!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Trip ID</th>
                    <th>Trip Name</th>
                    <th>Destination</th>
                    <th>Duration</th>
                    <th>Price Range</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map(trip => {
                    const prices = Object.values(trip.starting_price || {});
                    const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;

                    return (
                      <tr key={trip.id}>
                        <td>#{trip.id}</td>
                        <td>{trip.tripname}</td>
                        <td>{trip.location}</td>
                        <td>{trip.days}D/{trip.nights}N</td>
                        <td>
                          {trip.display_price ? (
                            `₹${trip.display_price}`
                          ) : (
                            `₹${lowestPrice.toLocaleString()}`
                          )}
                        </td>
                        <td>
                          <span className={`badge ${trip.status === 'Active' ? 'bg-success' : 'bg-warning'}`}>
                            {trip.status}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditTrip(trip.id)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTrip(trip.id, trip.tripname)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {deleteModal.show && (
        <div className="modal-backdrop" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setDeleteModal({ show: false, tripId: null, tripName: "" })}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete the trip "<strong>{deleteModal.tripName}</strong>" (ID: {deleteModal.tripId})?</p>
                  <p className="text-danger">This action cannot be undone!</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setDeleteModal({ show: false, tripId: null, tripName: "" })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDeleteTrip}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete Trip'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-backdrop" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal show" style={{ display: "block" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{modalTitle}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  {message.text && (
                    <div className={`alert alert-${message.type}`}>
                      {message.text}
                    </div>
                  )}

                  <div className="progress mb-4" style={{ height: "5px" }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${(activeTab / tabs.length) * 100}%` }}
                      aria-valuenow={(activeTab / tabs.length) * 100}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>

                  <ul className="nav nav-pills nav-justified mb-4">
                    {tabs.map(tab => (
                      <li className="nav-item" key={tab.id}>
                        <button
                          type="button"
                          className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={(e) => changeTab(e, tab.id)}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <div className={`circle-step ${activeTab >= tab.id ? 'active' : ''}`}>
                              {tab.id}
                            </div>
                            <span className="ms-2 d-none d-md-inline">{tab.name}</span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={(e) => {
                    if (e.nativeEvent.submitter && e.nativeEvent.submitter.type === 'submit') {
                      handleSubmit(e);
                    } else {
                      e.preventDefault();
                    }
                  }}>
                    {renderActiveTabContent()}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .circle-step {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: #e9ecef;
          color: #6c757d;
          font-weight: bold;
        }
        .circle-step.active {
          background-color: #0d6efd;
          color: white;
        }
        .nav-pills .nav-link {
          border-radius: 0.5rem;
          color: #6c757d;
        }
        .nav-pills .nav-link.active {
          background-color: #e7f1ff;
          color: #0d6efd;
        }
        .card-header {
          background-color: #f8f9fa;
        }
        .id-preview {
          margin-top: 8px;
          font-size: 0.875rem;
        }
        
        .trips-section .day-header {
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          overflow: hidden !important;
        }
        
        .trips-section .day-number {
          font-weight: 700 !important;
          color: #0d6efd !important;
          font-size: 1rem !important;
          margin-right: 8px !important;
          flex-shrink: 0 !important;
          background-color: #e7f1ff !important;
          padding: 2px 8px !important;
          border-radius: 4px !important;
        }
        
        .trips-section .day-title {
          font-size: 0.95rem !important;
          color: #343a40 !important;
          font-weight: 500 !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 75% !important;
        }
        
        .trips-section .card .card-header {
          padding: 0.7rem 0.75rem !important;
          border-bottom: 1px solid rgba(0,0,0,0.08) !important;
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
        }

        .trips-section .card-header .btn-outline-danger {
          padding: 0.2rem 0.5rem !important;
          margin-left: 8px !important;
        }
        
        .trips-section .card:hover {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
          transition: all 0.2s ease-in-out !important;
        }

        .date-input-wrapper {
          position: relative;
          cursor: pointer;
        }

        .date-input-wrapper .date-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          pointer-events: none;
        }

        .date-input-wrapper input {
          cursor: pointer;
        }

        .modal-body .form-control, 
        .modal-body .form-select {
          padding: 0.4rem 0.75rem;
          font-size: 0.9rem;
        }

        .modal-body .btn {
          padding: 0.4rem 0.75rem;
        }

        .modal-body .mb-3 {
          margin-bottom: 0.75rem !important;
        }

        .modal-body .mb-4 {
          margin-bottom: 1rem !important;
        }

        .modal-body .form-label {
          margin-bottom: 0.25rem;
          font-size: 0.9rem;
        }

        .modal-body textarea.form-control {
          min-height: 2.5rem;
        }

        .modal-body hr {
          margin: 0.75rem 0;
        }

        .modal-body h5 {
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .modal-body h6 {
          font-size: 0.9rem;
          margin-bottom: 0.4rem;
        }

        .modal-body .card {
          margin-bottom: 0.75rem;
        }

        .modal-body .card-header {
          padding: 0.5rem 0.75rem;
        }

        .modal-body .card-body {
          padding: 0.75rem;
        }

        .modal-body .alert {
          padding: 0.5rem 0.75rem;
          margin-bottom: 0.75rem;
        }

        .modal-body .input-group-text {
          padding: 0.4rem 0.75rem;
        }

        .modal-body p.text-muted {
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
        }

        .pricing-categories .row {
          margin-bottom: 0.4rem !important;
        }

        .meals-bg {
          background: #f3f7fa;
          border: 1px solid #e0e7ef;
          display: inline-block;
          min-width: 100px;
        }

        @media (max-width: 767px) {
          .pricing-item {
            margin-bottom: 15px;
            padding-bottom: 15px;
            border-bottom: 1px dashed #eee;
          }

          .pricing-item:last-child {
            border-bottom: none;
          }

          .pickup-points-table, .dropping-points-table {
            margin-bottom: 20px;
          }

          .input-group {
            flex-wrap: wrap;
          }

          .input-group .form-control {
            border-radius: 4px !important;
            margin-bottom: 8px;
          }

          .input-group .btn {
            border-radius: 4px !important;
            width: 100%;
          }

          .form-label {
            font-weight: 500;
          }

          .mb-3 {
            margin-bottom: 1.5rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Trips;
